import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from "prop-types";
import withRouter from '../Components/Common/withRouter';

//import Components
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import OpenOrdersOffcanvas from '../Components/Studio/OpenOrdersOffcanvas';
import ContextualHelpOffcanvas from '../Components/Studio/ContextualHelpOffcanvas';
import ImpersonationBanner from '../Components/Common/ImpersonationBanner';
import MembershipInviteWatcher from '../Components/Studio/MembershipInviteWatcher';
import ShellVisualPreviewBanner from '../Components/Studio/ShellVisualPreviewBanner';
import StudioSwitchBanner from '../Components/Studio/StudioSwitchBanner';
import SubscriptionBanner from '../Components/Studio/SubscriptionBanner';
import ErrorBoundary from '../Components/Common/ErrorBoundary';
import {
    applyShellVisualPreviewDom,
    readShellVisualPreview,
    SHELL_VISUAL_PREVIEW_EVENT,
    sidebarThemeForPreview,
} from '../helpers/shell_visual_preview';

//import actions
import {
    changeLayout,
    changeSidebarTheme,
    changeLayoutMode,
    changeLayoutThemeColor,
    changeLayoutTheme,
    changeLayoutWidth,
    changeLayoutPosition,
    changeTopbarTheme,
    changeLeftsidebarSizeType,
    changeLeftsidebarViewType,
    changeSidebarImageType,
    changeSidebarVisibility
} from "../slices/thunks";

//redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from 'reselect';

const Layout = (props : any) => {
    const [headerClass, setHeaderClass] = useState("");
    const dispatch : any = useDispatch();

    const selectLayoutState = (state : any) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutType: layout.layoutType,
            leftSidebarType: layout.leftSidebarType,
            layoutModeType: layout.layoutModeType,
            layoutThemeColorType: layout.layoutThemeColorType,
            layoutThemeType : layout.layoutThemeType,
            layoutWidthType: layout.layoutWidthType,
            layoutPositionType: layout.layoutPositionType,
            topbarThemeType: layout.topbarThemeType,
            leftsidbarSizeType: layout.leftsidbarSizeType,
            leftSidebarViewType: layout.leftSidebarViewType,
            leftSidebarImageType: layout.leftSidebarImageType,
            preloader: layout.preloader,
            sidebarVisibilitytype: layout.sidebarVisibilitytype,
        })
    );
    // Inside your component
    const {
        layoutType,
        leftSidebarType,
        layoutModeType,
        layoutThemeColorType,
        layoutThemeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype
    } = useSelector(selectLayoutProperties);

    /*
    layout settings
    */
    useEffect(() => {
        if (
            layoutType ||
            leftSidebarType ||
            layoutModeType ||
            layoutThemeType ||
            layoutThemeColorType ||
            layoutWidthType ||
            layoutPositionType ||
            topbarThemeType ||
            leftsidbarSizeType ||
            leftSidebarViewType ||
            leftSidebarImageType ||
            sidebarVisibilitytype
        ) {
            window.dispatchEvent(new Event('resize'));
            const previewVariant = readShellVisualPreview();
            const sidebarTheme = sidebarThemeForPreview(previewVariant);
            dispatch(changeLeftsidebarViewType(leftSidebarViewType));
            dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
            dispatch(changeSidebarTheme(sidebarTheme));
            dispatch(changeLayoutThemeColor(layoutThemeColorType));
            dispatch(changeLayoutTheme(layoutThemeType));
            dispatch(changeLayoutMode(layoutModeType));
            dispatch(changeLayoutWidth(layoutWidthType));
            dispatch(changeLayoutPosition(layoutPositionType));
            dispatch(changeTopbarTheme(topbarThemeType));
            dispatch(changeLayout(layoutType));
            dispatch(changeSidebarImageType(leftSidebarImageType));
            dispatch(changeSidebarVisibility(sidebarVisibilitytype));
            applyShellVisualPreviewDom(previewVariant);
        }
    }, [layoutType,
        leftSidebarType,
        layoutModeType,
        layoutThemeType,
        layoutThemeColorType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype,
        dispatch]);

    useEffect(() => {
        const syncPreview = () => {
            const previewVariant = readShellVisualPreview();
            applyShellVisualPreviewDom(previewVariant);
            dispatch(changeSidebarTheme(sidebarThemeForPreview(previewVariant)));
        };

        syncPreview();
        window.addEventListener(SHELL_VISUAL_PREVIEW_EVENT, syncPreview);
        window.addEventListener("storage", syncPreview);
        return () => {
            window.removeEventListener(SHELL_VISUAL_PREVIEW_EVENT, syncPreview);
            window.removeEventListener("storage", syncPreview);
        };
    }, [dispatch]);

    const scrollNavigation = useCallback(() => {
        const scrollup = document.documentElement.scrollTop;
        if (scrollup > 50) {
            setHeaderClass("topbar-shadow");
        } else {
            setHeaderClass("");
        }
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", scrollNavigation, true);
        return () => window.removeEventListener("scroll", scrollNavigation, true);
    }, [scrollNavigation]);

    useEffect(() => {
        const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement | null;
        if (!humberIcon) {
            return;
        }

        if (sidebarVisibilitytype === 'show' || layoutType === "vertical" || layoutType === "twocolumn") {
            humberIcon.classList.remove('open');
        } else {
            humberIcon.classList.add('open');
        }
    }, [sidebarVisibilitytype, layoutType]);

    return (
        <React.Fragment>
            <div id="layout-wrapper">
                <Header headerClass={headerClass} />
                <ShellVisualPreviewBanner />
                <SubscriptionBanner />
                <StudioSwitchBanner />
                <Sidebar
                    layoutType={layoutType}
                />
                <div className="main-content">
                    <ErrorBoundary resetKey={props.router?.location?.pathname}>
                        {props.children}
                    </ErrorBoundary>
                    <Footer />
                </div>
                {/* Fixed footer bar — BUG-F2-101; must not sit under logo/header */}
                <ImpersonationBanner />
            </div>
            <ContextualHelpOffcanvas />
            <OpenOrdersOffcanvas />
            <MembershipInviteWatcher />
        </React.Fragment>

    );
};

Layout.propTypes = {
    children: PropTypes.object,
};

export default withRouter(Layout);